// features/workstations/knowledge/kb-section.js
// ═══════════════════════════════════════════════════════════
// Knowledge Base — searchable reference library.
//
// V1: seeded from lib/intelligence/curriculum.js (40 trading
// concepts) + any user-saved entries. Search + category filter.
// Click an entry to expand its body.
//
// V1.5: "Save as KB" button on chat messages, finding cards,
// research items.
// V2: side-panel access from Chat (open KB without leaving
// the conversation).
// ═══════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';

const KIND_TONES = {
  concept:    'purple',
  study:      'teal',
  framework:  'gold',
  term:       'static',
  pattern:    'sage',
};

export default function KBSection({ workstation, userId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!workstation?.domain_key) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const qs = new URLSearchParams({
          domain: workstation.domain_key,
          kind: 'kb',
        });
        if (userId) qs.set('user_id', userId);
        const res = await fetch(`/api/workstations/knowledge?${qs}`, { cache: 'no-store' });
        if (cancelled) return;
        const data = res.ok ? await res.json() : { entries: [] };
        setEntries(data.entries || []);
      } catch (e) {
        console.warn('[knowledge/kb] load failed:', e?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workstation?.domain_key, userId]);

  // Filter in-memory (V1)
  const filtered = useMemo(() => {
    let out = entries;
    if (category !== 'all') {
      out = out.filter((e) => (e.category || '').toLowerCase() === category.toLowerCase());
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      out = out.filter((e) => {
        const hay = `${e.title} ${e.body_md || ''} ${(e.related_terms || []).join(' ')}`.toLowerCase();
        return hay.includes(s);
      });
    }
    return out;
  }, [entries, category, search]);

  const categories = useMemo(() => {
    const set = new Set();
    for (const e of entries) if (e.category) set.add(e.category);
    return Array.from(set).sort();
  }, [entries]);

  return (
    <div className="ws-kb-section">
      <div className="ws-kb-toolbar">
        <input
          type="text"
          className="ws-kb-search"
          placeholder="Search concepts, terms, frameworks…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="ws-kb-categories">
          <button
            type="button"
            className={`ws-kb-cat${category === 'all' ? ' active' : ''}`}
            onClick={() => setCategory('all')}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              className={`ws-kb-cat${category === c ? ' active' : ''}`}
              onClick={() => setCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="ws-loading">Loading knowledge base…</div>}

      {!loading && filtered.length === 0 && (
        <div className="ws-feed-empty">
          <div className="ws-feed-empty-title">No matching entries</div>
          <div className="ws-feed-empty-body">
            {search || category !== 'all'
              ? 'Try clearing your search or category filter.'
              : 'No knowledge entries for this domain yet. Save-from-chat ships in V1.5.'}
          </div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="ws-kb-list">
          {filtered.map((e) => (
            <KBEntry
              key={e.id}
              entry={e}
              expanded={expandedId === e.id}
              onToggle={() => setExpandedId(expandedId === e.id ? null : e.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function KBEntry({ entry, expanded, onToggle }) {
  const tone = KIND_TONES[entry.kind] || 'static';

  return (
    <article
      className={`ws-kb-entry${expanded ? ' is-expanded' : ''}`}
      onClick={onToggle}
    >
      <header className="ws-kb-entry-header">
        <span className={`ws-pill ws-pill-${tone}`}>{kindLabel(entry.kind)}</span>
        <h3 className="ws-kb-entry-title">{entry.title}</h3>
        <span className="ws-kb-entry-chev" aria-hidden="true">{expanded ? '▾' : '▸'}</span>
      </header>
      {expanded && (
        <div className="ws-kb-entry-body">
          <div className="ws-kb-entry-text">{entry.body_md}</div>
          {entry.related_terms?.length > 0 && (
            <div className="ws-kb-entry-related">
              <span className="ws-kb-entry-related-label">Related:</span>
              {entry.related_terms.map((t, i) => (
                <span key={i} className="ws-kb-related-chip">{t}</span>
              ))}
            </div>
          )}
          {entry.is_seed && (
            <div className="ws-kb-entry-seed-note">
              From the Shift Trading Curriculum · read-only · save-as-mine ships in V1.5
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function kindLabel(kind) {
  if (!kind) return 'Entry';
  return kind[0].toUpperCase() + kind.slice(1);
}
